import {
  Lit3formBlock,
  html,
  unsafeHTML,
  ifDefined,
} from '@Root/lib/lit-3form-block';
import { styles } from './css';
import {
  adobeCleanString,
  handleAdobeNavEvent,
} from '../../../helpers/adobe-analytics';

export class Edward extends Lit3formBlock {
  static get characterName() {
    return 'edward';
  }

  static get characterProperties() {
    return {
      cta: { type: String },
      _dynamicImageUrl: { type: String },
      _analyticsName: { type: String },
      _videoReady: { type: Boolean },
    };
  }

  static get characterStyles() {
    return [...styles];
  }

  constructor() {
    super();
    this._dynamicImageUrl = this.dynamicUrl;

    this._videoReady = false;

    if (this.hasMobileImage) {
      window.addEventListener('useMobileEvent', e => {
        const useMobile = e.detail?.useMobile;
        this._dynamicImageUrl = useMobile ? this.mobileImageUrl : this.imageUrl;
        this.requestUpdate();
      });
    }
    this.setupAnalyticsData();
    localStorage.removeItem('requestInfoData');
  }

  render() {
    return html`
      <div class="block-wrapper">
        <div class="block-grid edward-layout">
          <div class="media">
            ${this.mediaHTML}${this.captionHTML}
          </div>
          <div class="header">
            ${this.subheaderHTML} ${this.headerHTML}
          </div>
          ${this.descriptionHTML} ${this.ctaHTML} ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  get showEmbeddedVideo() {
    return !!this.content.media?.type?.includes('video');
  }

  get contentMedia() {
    return this.content.media || {};
  }

  get videoPosterUrl() {
    let posterUrl = this.content.image.poster_image;
    if (posterUrl) {
      posterUrl = this.imagebossUrl(posterUrl, null, 750);
    }
    return posterUrl || this.dynamicUrl || '';
  }

  _markVideoReady(event) {
    // This being triggered by @loadeddata @canplay and @playing might be
    // overkill, but it covers all bases just in case.
    if (this._videoReady) return;

    this._videoReady = true;

    // need to force the update here
    this.requestUpdate();

    const video = event.currentTarget;
    if (video && video.paused) {
      video.play?.().catch(() => {});
    }
  }

  get headerHTML() {
    return this.headerText
      ? html`
          <h1>${unsafeHTML(this.headerText)}</h1>
        `
      : ``;
  }

  get subheaderHTML() {
    return this.content.subheader
      ? html`
          <p class="subheader">${unsafeHTML(this.content.subheader)}</p>
        `
      : ``;
  }

  get descriptionHTML() {
    return this.descriptionText
      ? html`
          <p class="description">${unsafeHTML(this.descriptionText)}</p>
        `
      : ``;
  }

  get mediaHTML() {
    return this.showEmbeddedVideo
      ? html`
          <div class="video-wrap ${this._videoReady ? 'is-ready' : ''}">
            ${!!this.videoPosterUrl
              ? html`
                  <img
                    class="video-poster"
                    src=${this.videoPosterUrl}
                    alt=""
                    aria-hidden="true"
                  />
                `
              : html``}
            <video
              autoplay
              muted
              loop
              playsinline
              preload="auto"
              crossorigin="anonymous"
              @loadeddata=${this._markVideoReady}
              @canplay=${this._markVideoReady}
              @playing=${this._markVideoReady}
            >
              <source
                src="${this.contentMedia.src}"
                type="${this.contentMedia.type}"
              />
            </video>
          </div>
        `
      : html`
          <a href=${this.ctaUrl} @click="${this.mediaLinkClicked}">
            <img src=${this._dynamicImageUrl} />
          </a>
        `;
  }

  // Without a CTA the image links nowhere, so there is no click to report.
  mediaLinkClicked(event) {
    if (!this.hasCta) return;

    event.preventDefault();
    handleAdobeNavEvent({
      navString: this._analyticsName,
      destinationUrl: this.content.cta.url,
      event,
    });
  }

  get ctaHTML() {
    return this.hasCta
      ? html`
          <cta-button
            class="cta-btn"
            cta-data=${this.ctaInfo}
            cta-type="primary"
          ></cta-button>
        `
      : ``;
  }

  get ctaUrl() {
    return this.hasCta && this.content.cta.url !== '#contact-rep'
      ? this.content.cta.url
      : undefined;
  }

  get ctaInfo() {
    const ctaData = this.content.cta || {};
    ctaData.analyticsName = this._analyticsName;
    return JSON.stringify(ctaData);
  }

  get hasCta() {
    return this.content.cta && !!this.content.cta.text;
  }

  get dynamicUrl() {
    return this._useMobileLayout && this.hasMobileImage
      ? this.mobileImageUrl
      : this.imageUrl;
  }

  get imageUrl() {
    return this.imagebossUrl(this.content.image.src, null, 750);
  }

  get hasMobileImage() {
    return !!this.content.image.small_screen_image;
  }

  get mobileImageUrl() {
    return this.imagebossUrl(this.content.image.small_screen_image, 600, null);
  }

  get captionHTML() {
    return !!this.imageCaption
      ? html`
          <image-caption
            description=${this.imageCaption}
            file-url=${this.imageUrl || ''}
            placement=${this.headerText}
          ></image-caption>
        `
      : html``;
  }

  get imageCaption() {
    return this.content.caption
      ? this.withLineBreaks(this.content.caption)
      : '';
  }

  setupAnalyticsData() {
    const ctaData = this.content.cta || {};
    const cleanHeader = adobeCleanString(this.headerText);
    const cleanCtaText = adobeCleanString(ctaData.text);
    this._analyticsName = `NAV|Hero ${cleanHeader}|${cleanCtaText}`;
  }
}
Edward.defineElement();
